/* eslint-disable @typescript-eslint/no-unused-vars */
import React from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import { SkeletonText, SkeletonBlock } from "@ui-components/Skeleton/GenericSkeleton";
import { Fwaas, HostConfig } from "./FwaasData";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericCard from "@ui-components/Card/GenericCard";

export interface FwaasViewModalProps {
  isOpen: boolean;
  isLoading: boolean;
  fwaas: Fwaas | null;
  onClose: () => void;
}

export const FwaasViewModal: React.FC<FwaasViewModalProps> = ({
  isOpen,
  isLoading,
  fwaas,
  onClose,
}) => {
  return (
    <GenericModal isOpen={isOpen} onClose={onClose} title="View FWAAS Details" size="lg">
      {isLoading ? (
        <div className="space-y-6">
          <div>
            <SkeletonBlock h={20} w="30%" className="mb-3" />
            <div className="grid grid-cols-2 gap-4">
              {Array.from({ length: 10 }).map((_, idx) => (
                <SkeletonText key={idx} lines={2} lineHeight={14} />
              ))}
            </div>
          </div>
          <div>
            <SkeletonBlock h={20} w="40%" className="mb-3" />
            <div className="grid grid-cols-2 gap-4">
              {Array.from({ length: 3 }).map((_, idx) => (
                <SkeletonText key={idx} lines={2} lineHeight={14} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        fwaas && (
          <div className="space-y-6">
            {/* Main FWAAS Information */}
            <div>
              <h3 className="text-lg font-semibold mb-4 text-semantic-text-primary">
                FWAAS Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <GenericInput
                  label="ID"
                  value={String(fwaas.id || "")}
                  readOnly
                />
                <GenericInput label="Name" value={fwaas.name || ""} readOnly />
                <GenericInput
                  label="Subscription ID"
                  value={fwaas.subscriptionId || ""}
                  readOnly
                />
                <GenericInput
                  label="Tenant"
                  value={fwaas.tenant?.nameDisplay || fwaas.tenant?.Name || ""}
                  readOnly
                />
                <GenericInput
                  label="Status"
                  value={fwaas.isDraft ? "Draft" : "Active"}
                  readOnly
                />
                <GenericInput
                  label="Connections"
                  value={fwaas.connections || ""}
                  readOnly
                />
                <GenericInput
                  label="Terms"
                  value={fwaas.terms ? String(fwaas.terms) : "N/A"}
                  readOnly
                />
                <GenericInput
                  label="Auto Renew"
                  value={fwaas.autoRenew !== null ? (fwaas.autoRenew ? "Yes" : "No") : "N/A"}
                  readOnly
                />
                <GenericInput
                  label="Created At"
                  value={fwaas.createdAt ? new Date(fwaas.createdAt).toLocaleString() : ""}
                  readOnly
                />
                <GenericInput
                  label="Updated At"
                  value={fwaas.updatedAt ? new Date(fwaas.updatedAt).toLocaleString() : ""}
                  readOnly
                />
              </div>
            </div>

            {/* Host Configs */}
            {fwaas.hostConfigs && fwaas.hostConfigs.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-4 text-semantic-text-primary">
                  Host Configurations
                </h3>
                <div className="space-y-4">
                  {fwaas.hostConfigs.map((hostConfig, index) => (
                    <div key={index} className="border border-semantic-border-secondary rounded p-4">
                      <div className="grid grid-cols-2 gap-4">
                        <GenericInput
                          label="Host"
                          value={hostConfig.host || ""}
                          readOnly
                        />
                        <GenericInput
                          label="PVE Host"
                          value={hostConfig.pve_host || ""}
                          readOnly
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Odoo Metadata */}
            {fwaas.odooMetadata && (
              <div>
                <h3 className="text-lg font-semibold mb-4 text-semantic-text-primary">
                  Odoo Metadata
                </h3>
                <div className="border border-semantic-border-secondary rounded p-4">
                  <pre className="text-sm text-semantic-text-secondary whitespace-pre-wrap">
                    {JSON.stringify(fwaas.odooMetadata, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
        )
      )}
    </GenericModal>
  );
};

export interface FwaasEditModalProps {
  isOpen: boolean;
  isLoading: boolean;
  isCreateMode: boolean;
  fwaas: Fwaas | null;
  createForm?: {
    tenantId: number | null;
    name: string;
    subscriptionId: string;
    terms: number | null;
    autoRenew: boolean | null;
    connections: string;
  };
  tenantOptions?: Array<{ id: number; name: string; displayName: string }>;
  hasChanges: boolean;
  isFormValid: boolean;
  isSaving: boolean;
  isCreating: boolean;
  getTenantDisplayNameById?: (id: number | null) => string;
  onClose: () => void;
  onSave: () => void;
  onFieldChange: (field: string, value: string | number | boolean | null | HostConfig[]) => void;
  onCreateFieldChange?: (field: string, value: string | number | boolean | null) => void;
}

export const FwaasEditModal: React.FC<FwaasEditModalProps> = ({
  isOpen,
  isLoading,
  isCreateMode,
  fwaas,
  createForm,
  tenantOptions = [],
  hasChanges,
  isFormValid,
  isSaving,
  isCreating,
  getTenantDisplayNameById,
  onClose,
  onSave,
  onFieldChange,
  onCreateFieldChange,
}) => {
  const tenantNames = tenantOptions.map((t) => t.displayName);
  
  const handleTenantSelect = (displayName: string) => {
    const tenant = tenantOptions.find((t) => t.displayName === displayName);
    if (tenant && onCreateFieldChange) {
      onCreateFieldChange("tenantId", tenant.id);
    }
  };

  return (
    <GenericModal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={isCreateMode ? "Create FWAAS" : "Edit FWAAS"} 
      size="lg"
    >
      {isLoading ? (
        <div className="space-y-6">
          <SkeletonBlock h={20} w="30%" className="mb-3" />
          <div className="grid grid-cols-2 gap-4">
            {Array.from({ length: 10 }).map((_, idx) => (
              <SkeletonText key={idx} lines={2} lineHeight={14} />
            ))}
          </div>
        </div>
      ) : (
        (isCreateMode ? createForm !== undefined : fwaas) && (
          <div className="space-y-6">
            {isCreateMode ? (
              <>
                {/* Tenant dropdown in its own row */}
                <div className="flex flex-col">
                  <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                    Tenant *
                  </label>
                  <GenericDropdown
                    items={tenantNames}
                    value={getTenantDisplayNameById ? getTenantDisplayNameById(createForm?.tenantId || null) : ""}
                    defaultLabel="Select Tenant"
                    onSelect={handleTenantSelect}
                    minWidthClass="min-w-[180px]"
                    maxWidthClass="max-w-[240px]"
                    dropdownMaxHeightClass="max-h-48"
                    fixedPosition={true}
                  />
                </div>
                
                <GenericCard
                  isCollapsible={false}
                  variant="secondary"
                >
                {/* Other fields in grid */}
                <div className="grid grid-cols-2 gap-4 p-5">
                  <GenericInput
                    label="Name *"
                    value={createForm?.name || ""}
                    onChange={(value) => onCreateFieldChange && onCreateFieldChange("name", value)}
                  />
                  <GenericInput
                    label="Subscription ID *"
                    value={createForm?.subscriptionId || ""}
                    onChange={(value) => onCreateFieldChange && onCreateFieldChange("subscriptionId", value)}
                  />
                  <GenericInput
                    label="Connections *"
                    value={createForm?.connections || ""}
                    onChange={(value) => onCreateFieldChange && onCreateFieldChange("connections", value)}
                    tooltipText="List of connections separated by semicolons (e.g., I-1;CC-1)"
                  />
                  <GenericInput
                    label="Terms *"
                    value={createForm?.terms ? String(createForm.terms) : ""}
                    onChange={(value) => onCreateFieldChange && onCreateFieldChange("terms", value ? Number(value) : null)}
                    type="number"
                    tooltipText="Contract duration in months"
                  />
                  <div className="flex flex-col">
                    <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                      Auto Renew *
                    </label>
                    <GenericDropdown
                      items={["Yes", "No"]}
                      value={createForm?.autoRenew === null ? "" : (createForm?.autoRenew ? "Yes" : "No")}
                      defaultLabel="Select Auto Renew"
                      onSelect={(val) => onCreateFieldChange && onCreateFieldChange("autoRenew", val === "Yes")}
                      minWidthClass="min-w-[180px]"
                      maxWidthClass="max-w-[240px]"
                    />
                  </div>
                </div>
                </GenericCard>
              </>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <GenericInput
                  label="ID"
                  value={String(fwaas?.id || "")}
                  readOnly
                />
                <GenericInput
                  label="Name"
                  value={fwaas?.name || ""}
                  onChange={(value) => onFieldChange("name", value)}
                />
                <GenericInput
                  label="Subscription ID"
                  value={fwaas?.subscriptionId || ""}
                  onChange={(value) => onFieldChange("subscriptionId", value)}
                />
                <GenericInput
                  label="Connections"
                  value={fwaas?.connections || ""}
                  onChange={(value) => onFieldChange("connections", value)}
                  tooltipText="List of connections separated by semicolons (e.g., I-1;CC-1)"
                />
                <GenericInput
                  label="Terms"
                  value={fwaas?.terms ? String(fwaas.terms) : ""}
                  onChange={(value) => onFieldChange("terms", value ? Number(value) : null)}
                  tooltipText="Contract duration in months"
                />
                <GenericInput
                  label="Tenant"
                  value={fwaas?.tenant?.nameDisplay || fwaas?.tenant?.Name || ""}
                  readOnly
                />
                <div className="flex flex-col">
                  <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                    Auto Renew
                  </label>
                  <GenericDropdown
                    items={["Yes", "No"]}
                    value={fwaas?.autoRenew === null ? "" : (fwaas?.autoRenew ? "Yes" : "No")}
                    defaultLabel="Select Auto Renew"
                    onSelect={(val) => onFieldChange("autoRenew", val === "Yes")}
                    minWidthClass="min-w-[180px]"
                    maxWidthClass="max-w-[240px]"
                  />
                </div>
              </div>
            )}

            {/* Host Configs - Only show in edit mode, not create */}
            {!isCreateMode && fwaas?.hostConfigs && fwaas.hostConfigs.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-4 text-semantic-text-primary">
                  Host Configurations
                </h3>
                <div className="space-y-4">
                  {fwaas.hostConfigs.map((hostConfig, index) => (
                    <div key={index} className="border border-semantic-border-secondary rounded p-4">
                      <div className="grid grid-cols-2 gap-4">
                        <GenericInput
                          label="Host"
                          value={hostConfig.host || ""}
                          onChange={(value) => {
                            const updatedHostConfigs = [...fwaas.hostConfigs];
                            updatedHostConfigs[index] = { ...hostConfig, host: value };
                            onFieldChange("hostConfigs", updatedHostConfigs);
                          }}
                        />
                        <GenericInput
                          label="PVE Host"
                          value={hostConfig.pve_host || ""}
                          onChange={(value) => {
                            const updatedHostConfigs = [...fwaas.hostConfigs];
                            updatedHostConfigs[index] = { ...hostConfig, pve_host: value };
                            onFieldChange("hostConfigs", updatedHostConfigs);
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-6">
              <GenericButton
                label="Cancel"
                variant="secondary"
                onClick={onClose}
                disabled={isSaving || isCreating}
              />
              <GenericButton
                label={isCreateMode ? "Create" : "Save"}
                variant="brand"
                onClick={onSave}
                disabled={isCreateMode ? !isFormValid || isCreating : !hasChanges || isSaving}
                loading={isCreateMode ? isCreating : isSaving}
              />              
            </div>
          </div>
        )
      )}
    </GenericModal>
  );
};